import { useGetPaginatedCategoriesQuery } from "@/api/ticketApi/category";
import { useRemoveCategoryFromGroupMutation } from "@/api/groupApi/groupCategory";
import DataTable from "../Table";
import { useState } from "react";
import { Button } from "flowbite-react";
import { HiX } from "react-icons/hi";
import { DataGridPagination, Category } from "@/sharedTypes";
import LoadingPage from "../LoadingPage";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

const GroupCategoriesTable = ({ groupId }: { groupId: string }) => {
  const { t } = useTranslation(["group", "category"]);
  const user = useCurrentUser();
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data: categoriesData, isLoading } = useGetPaginatedCategoriesQuery({
    filters: { group_id: groupId },
    pagination: { page, pageSize },
  });

  const [removeCategory] = useRemoveCategoryFromGroupMutation();

  const handleRemove = (categoryId: number) => {
    removeCategory({ groupId, categoryId })
      .unwrap()
      .then(() => toast.success(t("group:categoryRemoved")))
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const columns = [
    { key: "category_name", label: t("category:categoryName") },
    { key: "category_description", label: t("category:categoryDescription") },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("category:actions"),
            render: (row: Category) => (
              <Button
                size="xs"
                color="light"
                onClick={() => handleRemove(row.category_id)}
                title={t("group:removeCategory")}
              >
                <HiX className="h-4 w-4 text-red-600" />
              </Button>
            ),
          },
        ]
      : []),
  ];

  const paginationData = categoriesData?.pagination || categoriesData || {};

  const pagination: DataGridPagination = {
    pageIndex: paginationData.pageIndex ?? paginationData.page ?? 1,
    pageSize: paginationData.pageSize ?? 10,
    totalPages: paginationData.totalPages ?? 1,
    totalItems: paginationData.totalItems ?? paginationData.total ?? 0,
  };

  if (isLoading) return <LoadingPage />;

  return (
    <DataTable
      columns={columns}
      data={categoriesData?.items || []}
      pagination={pagination}
      onPageChange={setPage}
    />
  );
};

export default GroupCategoriesTable;
